import { useState, useRef, useEffect, useCallback } from "react";
import { safeGetUserMedia } from "@/lib/browserCapabilities";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Shield, UserPlus, Camera, Trash2, Edit, ChevronLeft, Users,
  Search, Loader2, AlertTriangle, CheckCircle2, Fingerprint, XCircle,
} from "lucide-react";
import { useLocation } from "wouter";
import CommandNav from "@/components/CommandNav";

// face-api.js import
import * as faceapi from "@vladmandic/face-api";

const FACE_MODEL_URL = "/api/face-models";

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: "Ativo", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  inactive: { label: "Inativo", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
  flagged: { label: "Sinalizado", color: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const CATEGORY_LABELS: Record<string, string> = {
  staff: "Colaborador", visitor: "Visitante", vip: "VIP", restricted: "Restrito", demo: "Demonstração",
};

// Load face-api models once
let modelsLoaded = false;
let modelsLoading = false;
const loadModels = async (): Promise<boolean> => {
  if (modelsLoaded) return true;
  if (modelsLoading) {
    // Wait for ongoing load
    for (let i = 0; i < 50; i++) {
      await new Promise(r => setTimeout(r, 200));
      if (modelsLoaded) return true;
    }
    return false;
  }
  modelsLoading = true;
  try {
    await faceapi.nets.ssdMobilenetv1.loadFromUri(FACE_MODEL_URL);
    await faceapi.nets.faceLandmark68Net.loadFromUri(FACE_MODEL_URL);
    await faceapi.nets.faceRecognitionNet.loadFromUri(FACE_MODEL_URL);
    modelsLoaded = true;
    console.log("[FaceAPI] Models loaded for person registration");
    return true;
  } catch (err) {
    console.error("[FaceAPI] Failed to load models:", err);
    modelsLoading = false;
    return false;
  }
};

// Extract face descriptor from an image element — rejects multiple faces
interface DescriptorResult {
  descriptor: number[] | null;
  faceCount: number;
  error?: string;
}

async function extractDescriptorSafe(imgEl: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement): Promise<DescriptorResult> {
  try {
    // Detect ALL faces to check for multiples
    const allDetections = await faceapi
      .detectAllFaces(imgEl, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.3 }))
      .withFaceLandmarks()
      .withFaceDescriptors();

    if (allDetections.length === 0) {
      return { descriptor: null, faceCount: 0, error: "Nenhum rosto detectado na imagem." };
    }

    if (allDetections.length > 1) {
      return { descriptor: null, faceCount: allDetections.length, error: `Foram detectados ${allDetections.length} rostos. A foto deve conter apenas 1 rosto.` };
    }

    // Exactly 1 face — check quality (detection score)
    const det = allDetections[0];
    const score = det.detection.score;
    if (score < 0.5) {
      return { descriptor: null, faceCount: 1, error: `Qualidade insuficiente (${(score * 100).toFixed(0)}%). Tente uma foto com melhor iluminação e rosto frontal.` };
    }

    return { descriptor: Array.from(det.descriptor), faceCount: 1 };
  } catch (err) {
    console.error("[FaceAPI] Descriptor extraction failed:", err);
    return { descriptor: null, faceCount: 0, error: "Erro ao processar biometria facial." };
  }
}

export default function Persons() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<any>(null);

  const { data: persons, isLoading, refetch } = trpc.person.list.useQuery();
  const createMutation = trpc.person.create.useMutation({
    onSuccess: () => { refetch(); setDialogOpen(false); toast.success("Pessoa cadastrada com sucesso"); },
    onError: (err: any) => toast.error("Erro ao cadastrar: " + err.message),
  });
  const deleteMutation = trpc.person.delete.useMutation({
    onSuccess: () => { refetch(); toast.success("Pessoa removida"); },
    onError: (err: any) => toast.error("Erro ao remover: " + err.message),
  });
  const updateMutation = trpc.person.update.useMutation({
    onSuccess: () => { refetch(); setDialogOpen(false); setEditingPerson(null); toast.success("Cadastro atualizado"); },
    onError: (err: any) => toast.error("Erro ao atualizar: " + err.message),
  });

  const filtered = persons?.filter(p =>
    p.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.identification && p.identification.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header — ELLA Command Platform */}
      <div className="sticky top-0 z-50">
        <CommandNav
          pageTitle="Banco Facial"
          rightContent={
            <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingPerson(null); }}>
              <DialogTrigger asChild>
                <Button style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #ec4899 100%)" }} className="text-white border-0 hover:opacity-90 text-xs h-8 px-3">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Cadastrar Pessoa
                </Button>
              </DialogTrigger>
              <PersonFormDialog
                editing={editingPerson}
                onSubmit={(data) => {
                  if (editingPerson) {
                    updateMutation.mutate({ id: editingPerson.id, ...data });
                  } else {
                    createMutation.mutate(data);
                  }
                }}
                loading={createMutation.isPending || updateMutation.isPending}
              />
            </Dialog>
          }
        />
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* LGPD Banner */}
        <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-amber-400 mb-1">Aviso LGPD — Ambiente de Demonstração</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Este módulo de reconhecimento facial opera exclusivamente em ambiente de demonstração controlado.
                Todos os dados biométricos são processados com consentimento explícito e armazenados de forma segura.
                Em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018).
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Cadastrados" value={persons?.length || 0} />
          <StatCard label="Ativos" value={persons?.filter(p => p.status === "active").length || 0} color="text-green-400" />
          <StatCard label="Sinalizados" value={persons?.filter(p => p.status === "flagged").length || 0} color="text-red-400" />
          <StatCard label="Com Biometria" value={persons?.filter(p => p.faceDescriptor).length || 0} color="text-ella-purple-light" />
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou identificação..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-card border-border/50"
          />
        </div>

        {/* Person Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-ella-purple-light" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma pessoa cadastrada</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Cadastre a primeira pessoa para iniciar o reconhecimento facial.
            </p>
            <Button onClick={() => setDialogOpen(true)} className="ella-gradient text-white border-0">
              <UserPlus className="w-4 h-4 mr-2" /> Cadastrar Primeira Pessoa
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((person) => (
              <div key={person.id} className="bg-card border border-border/50 rounded-lg p-4 hover:border-ella-purple/30 transition-colors group">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary shrink-0">
                    {person.photoUrl ? (
                      <img src={person.photoUrl} alt={person.fullName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <Users className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm truncate">{person.fullName}</h3>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_LABELS[person.status || "active"]?.color}`}>
                        {STATUS_LABELS[person.status || "active"]?.label}
                      </Badge>
                    </div>
                    {person.role && <p className="text-xs text-muted-foreground mb-0.5">{person.role}</p>}
                    {person.identification && <p className="text-[10px] text-muted-foreground/70">ID: {person.identification}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border/50">
                        {CATEGORY_LABELS[person.category || "demo"]}
                      </Badge>
                      {!!person.faceDescriptor && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-ella-purple/10 text-ella-purple-light border-ella-purple/30">
                          <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Biometria
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-border/30 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="outline" size="sm"
                    className="flex-1 text-xs h-7"
                    onClick={() => { setEditingPerson(person); setDialogOpen(true); }}
                  >
                    <Edit className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    className="text-xs h-7 text-red-400 border-red-400/30 hover:bg-red-400/10"
                    onClick={() => { if (confirm("Remover esta pessoa?")) deleteMutation.mutate({ id: person.id }); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-card border border-border/50 rounded-lg p-4">
      <div className={`text-2xl font-bold ${color || "text-foreground"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {value}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function PersonFormDialog({ editing, onSubmit, loading }: {
  editing: any;
  onSubmit: (data: any) => void;
  loading: boolean;
}) {
  const [fullName, setFullName] = useState(editing?.fullName || "");
  const [role, setRole] = useState(editing?.role || "");
  const [identification, setIdentification] = useState(editing?.identification || "");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [status, setStatus] = useState(editing?.status || "active");
  const [category, setCategory] = useState(editing?.category || "demo");
  const [photoPreview, setPhotoPreview] = useState<string | null>(editing?.photoUrl || null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [faceDescriptor, setFaceDescriptor] = useState<number[] | null>(null);
  const [descriptorStatus, setDescriptorStatus] = useState<"idle" | "loading" | "success" | "failed">("idle");
  const [useCameraMode, setUseCameraMode] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // If editing and person already has descriptor, show success
  useEffect(() => {
    if (editing?.faceDescriptor) {
      setDescriptorStatus("success");
    }
  }, [editing]);

  // Extract face descriptor from image — with multi-face rejection
  const extractFromImage = useCallback(async (dataUrl: string) => {
    setDescriptorStatus("loading");
    try {
      const loaded = await loadModels();
      if (!loaded) {
        setDescriptorStatus("failed");
        toast.error("Falha ao carregar modelos de reconhecimento facial");
        return;
      }

      // Create an image element to process
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = dataUrl;
      });

      const result = await extractDescriptorSafe(img);

      if (result.descriptor) {
        setFaceDescriptor(result.descriptor);
        setDescriptorStatus("success");
        console.log("[FaceAPI] Descriptor extracted successfully:", result.descriptor.length, "dimensions");
        toast.success("Biometria facial extraída com sucesso! (1 rosto detectado)");
      } else {
        setDescriptorStatus("failed");
        setFaceDescriptor(null);
        toast.error(result.error || "Nenhum rosto detectado na imagem.");
      }
    } catch (err) {
      console.error("[FaceAPI] Extraction error:", err);
      setDescriptorStatus("failed");
      setFaceDescriptor(null);
      toast.error("Erro ao processar biometria facial");
    }
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setPhotoPreview(result);
      setPhotoBase64(result);
      // Automatically extract face descriptor
      extractFromImage(result);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await safeGetUserMedia({ video: { facingMode: "user", width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setUseCameraMode(true);
    } catch (err) {
      toast.error("Erro ao acessar câmera");
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setPhotoPreview(dataUrl);
    setPhotoBase64(dataUrl);
    stopCamera();
    // Automatically extract face descriptor
    extractFromImage(dataUrl);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setUseCameraMode(false);
  };

  const handleSubmit = () => {
    if (!fullName.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!photoBase64 && !editing?.photoUrl) {
      toast.error("Foto é obrigatória para o cadastro facial");
      return;
    }
    if (!faceDescriptor && !editing?.faceDescriptor && descriptorStatus !== "loading") {
      toast.error("Biometria facial não extraída. Aguarde o processamento ou tente outra foto.");
      return;
    }

    const data: any = {
      fullName: fullName.trim(),
      role: role || undefined,
      identification: identification || undefined,
      notes: notes || undefined,
      status,
      category,
      photoBase64: photoBase64 || undefined,
    };

    // Send face descriptor if we have a new one
    if (faceDescriptor) {
      data.faceDescriptor = faceDescriptor;
    }

    onSubmit(data);
  };

  return (
    <DialogContent className="sm:max-w-lg bg-card border-border/50 max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {editing ? "Editar Cadastro" : "Cadastrar Pessoa"}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-2">
        {/* Photo */}
        <div className="flex flex-col items-center gap-3">
          {useCameraMode ? (
            <div className="relative w-40 h-40 rounded-lg overflow-hidden bg-black">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-40 h-40 rounded-lg overflow-hidden bg-secondary border-2 border-dashed border-border/50 flex items-center justify-center">
              {photoPreview ? (
                <img ref={imgRef} src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <Users className="w-10 h-10 text-muted-foreground/30" />
              )}
            </div>
          )}

          {/* Face Descriptor Status */}
          <div className="flex items-center gap-2">
            {descriptorStatus === "loading" && (
              <div className="flex items-center gap-1.5 text-amber-400 text-xs">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Extraindo biometria facial...</span>
              </div>
            )}
            {descriptorStatus === "success" && (
              <div className="flex items-center gap-1.5 text-green-400 text-xs">
                <Fingerprint className="w-3 h-3" />
                <span>Biometria facial extraída</span>
                <CheckCircle2 className="w-3 h-3" />
              </div>
            )}
            {descriptorStatus === "failed" && (
              <div className="flex items-center gap-1.5 text-red-400 text-xs">
                <XCircle className="w-3 h-3" />
                <span>Falha na extração — tente outra foto</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {useCameraMode ? (
              <>
                <Button size="sm" onClick={capturePhoto} className="ella-gradient text-white border-0 text-xs h-7">
                  <Camera className="w-3 h-3 mr-1" /> Capturar
                </Button>
                <Button size="sm" variant="outline" onClick={stopCamera} className="text-xs h-7">Cancelar</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={startCamera} className="text-xs h-7">
                  <Camera className="w-3 h-3 mr-1" /> Câmera
                </Button>
                <Label htmlFor="photo-upload" className="cursor-pointer">
                  <div className="inline-flex items-center justify-center h-7 px-3 text-xs border border-border rounded-md hover:bg-accent transition-colors">
                    Upload
                  </div>
                </Label>
                <input id="photo-upload" type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
              </>
            )}
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Nome Completo *</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Nome completo" className="mt-1 bg-background" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Cargo / Perfil</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Ex: Segurança" className="mt-1 bg-background" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Identificação</Label>
            <Input value={identification} onChange={(e) => setIdentification(e.target.value)} placeholder="Ex: CPF, matrícula" className="mt-1 bg-background" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1 bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
                <SelectItem value="flagged">Sinalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1 bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="demo">Demonstração</SelectItem>
                <SelectItem value="staff">Colaborador</SelectItem>
                <SelectItem value="visitor">Visitante</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="restricted">Restrito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Observações</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observações adicionais..." className="mt-1 bg-background min-h-[60px]" />
          </div>
        </div>
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline" className="text-xs">Cancelar</Button>
        </DialogClose>
        <Button
          onClick={handleSubmit}
          disabled={loading || descriptorStatus === "loading"}
          className="ella-gradient text-white border-0 hover:opacity-90 text-xs"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          {descriptorStatus === "loading" ? "Processando biometria..." : editing ? "Salvar Alterações" : "Cadastrar"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
